/**
 * English copy for the Ambassador candidate journey.
 *
 * Every string is reached through a step, question or reference key that the API
 * supplies, so a second locale is a second file of the same shape rather than a
 * change to any component. Nothing here is a commercial term: rates, currency and
 * worked examples all arrive from the server as data.
 */

export interface NamedItem {
  readonly title: string;
  readonly detail?: string;
}

export interface StepCopy {
  readonly eyebrow?: string;
  readonly title: string;
  readonly lede?: string;
  readonly body?: readonly string[];
  readonly highlight?: string;
  readonly listTitle?: string;
  readonly list?: readonly NamedItem[];
  readonly secondaryTitle?: string;
  readonly secondaryList?: readonly NamedItem[];
  readonly closing?: readonly string[];
  readonly cta: string;
}

export const brand = {
  name: "Zhatura",
  wordmark: "ZHATURA",
  skipToContent: "Skip to the invitation",
};

export const territoryNames: Readonly<Record<string, string>> = {
  "IN-TN": "Tamil Nadu",
  "IN-KL": "Kerala",
  "IN-KA": "Karnataka",
  IN: "India",
};

export const roleNames: Readonly<Record<string, string>> = {
  GROWTH_PARTNER: "Growth Partner",
  GROWTH_LEADER: "Growth Leader",
  AMBASSADOR: "Ambassador",
};

export const foundingRoleNames: Readonly<Record<string, string>> = {
  GROWTH_PARTNER: "Founding Growth Partner",
  GROWTH_LEADER: "Founding Growth Leader",
  AMBASSADOR: "Founding Ambassador",
};

export const segmentNames: Readonly<Record<string, string>> = {
  general: "General",
  academies: "Academies",
  education: "Education",
  creators: "Creators",
};

export const ambassadorTypes: Readonly<Record<string, NamedItem>> = {
  PLAYER: {
    title: "Player Ambassador",
    detail: "Players who bring other players with them.",
  },
  PARENT: {
    title: "Parent Ambassador",
    detail: "Parents other parents already turn to for advice.",
  },
  COACH: {
    title: "Coach Ambassador",
    detail: "Coaches whose recommendation carries weight.",
  },
  ACADEMY: {
    title: "Academy Ambassador",
    detail: "Academies building structured chess programmes.",
  },
  EDUCATION: {
    title: "Education Ambassador",
    detail: "Schools, colleges and universities.",
  },
  COMMUNITY: {
    title: "Community Ambassador",
    detail: "Clubs, tournaments and local chess communities.",
  },
  CREATOR: {
    title: "Creator Ambassador",
    detail: "Creators with a chess audience that trusts them.",
  },
  INFLUENCE: {
    title: "Influence Ambassador",
    detail: "Recognised figures who can open doors.",
  },
};

export const ambassadorGrades: Readonly<Record<string, NamedItem>> = {
  A1: { title: "Ambassador", detail: "A1" },
  A2: { title: "Ambassador Plus", detail: "A2" },
  A3: { title: "Ambassador Pro", detail: "A3" },
  A4: { title: "Ambassador Elite", detail: "A4" },
  A5: { title: "Ambassador Legend", detail: "A5" },
};

export const compensationComponents: Readonly<Record<string, NamedItem>> = {
  "fixed-compensation": {
    title: "Fixed Compensation",
    detail: "A contractual amount, agreed individually.",
  },
  "direct-commission": {
    title: "Direct Commission",
    detail: "A share of subscription revenue you bring in directly.",
  },
  "network-override": {
    title: "Network Override",
    detail: "A share of revenue created by the network you build.",
  },
  "performance-bonus": {
    title: "Performance and Campaign Bonuses",
    detail: "Tied to agreed outcomes and campaigns.",
  },
};

export const questions: Readonly<Record<string, string>> = {
  "first-ninety-days":
    "If you were helping launch Zhatura in {{Territory}}, what would you prioritise during the first 90 days?",
  "communities-to-approach":
    "Which chess communities, academies, schools, tournaments, cities or regions would you approach first — and why?",
  "ideal-ambassadors":
    "What kinds of people do you believe would make excellent Zhatura Ambassadors?",
  "opportunities-and-challenges":
    "What opportunities or challenges do you see in building this network in {{Territory}}?",
  "support-needed": "What would you need from Zhatura to succeed in this role?",
};

export const steps: Readonly<Record<string, StepCopy>> = {
  "private-invitation": {
    eyebrow: "Private invitation",
    title: "Hello {{FirstName}},",
    lede: "You've been invited to explore a founding opportunity with Zhatura.",
    body: [
      "Zhatura is preparing to build a new AI-powered chess development ecosystem for players, parents, coaches, academies and chess communities.",
      "As we begin our journey in {{Territory}}, we're inviting a small number of people to explore the opportunity to help build our growth network from the beginning.",
    ],
    highlight:
      "This isn't simply about promoting Zhatura. It's about helping build the community that grows with it.",
    listTitle: "What you'll discover",
    list: [
      { title: "The Vision" },
      { title: "The Opportunity" },
      { title: "Your Potential Role" },
      { title: "The Support" },
    ],
    cta: "Explore the opportunity",
  },
  "meet-zhatura": {
    eyebrow: "Meet Zhatura",
    title: "Building a better journey through chess.",
    body: [
      "Chess development is fragmented. Playing happens in one place, learning in another, coaching somewhere else again, and a parent trying to follow their child's progress has to piece it together themselves.",
      "Zhatura brings the journey together, so that practice, learning, coaching and competition all inform each other.",
    ],
    listTitle: "The ecosystem",
    list: [
      {
        title: "Play",
        detail: "Real games, fair matchmaking, honest ratings.",
      },
      {
        title: "Learn",
        detail: "Structured training that adapts to the player.",
      },
      {
        title: "AI Coach",
        detail: "Explanations grounded in the actual game.",
      },
      {
        title: "Analyze",
        detail: "Deterministic engine analysis, not guesswork.",
      },
      { title: "Compete", detail: "Tournaments and events that count." },
      {
        title: "Human Coaching",
        detail: "Real coaches, supported not replaced.",
      },
      {
        title: "Parents",
        detail: "Visibility appropriate to a child's progress.",
      },
      {
        title: "Academies",
        detail: "Tools for the people already teaching chess.",
      },
    ],
    highlight: "More than another chess app.",
    closing: [
      "Technology should strengthen the chess ecosystem — not replace it.",
      "AI explains and suggests. Human coaches teach and judge. Academies build programmes. Families support players. Communities create the events that make chess worth playing. Each of those roles is stronger when the others work well.",
    ],
    cta: "Why {{Territory}}?",
  },
  "why-this-market": {
    eyebrow: "Why {{Territory}}",
    title: "Every global journey needs a strong first community.",
    body: [
      "India is our first major market, and {{Territory}} is where we start. Not because it is the largest opportunity, but because it is the one where we can build carefully, learn honestly and earn trust before we scale.",
    ],
    listTitle: "What we're building on",
    list: [
      {
        title: "Players and parents",
        detail: "A deep and serious chess culture.",
      },
      {
        title: "Coaches and academies",
        detail: "Established teaching already working.",
      },
      {
        title: "Schools and universities",
        detail: "Chess inside education, not beside it.",
      },
      {
        title: "Tournaments and communities",
        detail: "A calendar that actually runs.",
      },
    ],
    highlight: "Build it locally. Learn from it. Scale what works.",
    closing: [
      "What works in {{Territory}} can expand across India and, in time, internationally. What does not work here gets fixed here.",
      "The people who help establish this first ecosystem will always be part of Zhatura's founding growth story.",
    ],
    cta: "See how we plan to grow",
  },
  "ambassador-vision": {
    eyebrow: "The Ambassador vision",
    title:
      "We believe trusted people can build stronger growth than advertising alone.",
    body: [
      "Chess decisions are made through relationships. A parent asks another parent. A player brings a friend. An academy recommends what it already uses. A creator's audience listens because they have earned it. A community leader's word carries further than any advertisement.",
    ],
    listTitle: "Where trust already flows",
    list: [
      { title: "Parents", detail: "to coaches" },
      { title: "Players", detail: "to players" },
      { title: "Academies", detail: "to communities" },
      { title: "Creators", detail: "to audiences" },
      { title: "Community leaders", detail: "to local networks" },
    ],
    secondaryTitle: "Zhatura Ambassadors",
    secondaryList: [
      {
        title: "A network of people",
        detail:
          "who believe Zhatura can create value for their communities, and who share in the economic value they help create.",
      },
      {
        title: "This is not recruitment-driven economics.",
        detail:
          "Ambassadors do not earn recurring income simply because they recruit other Ambassadors.",
      },
      {
        title: "Sustainable earnings originate from real customers",
        detail: "paying for Zhatura subscriptions.",
      },
    ],
    highlight: "Zhatura wins. Ambassador wins. Customer wins.",
    closing: ["Win. Win. Win."],
    cta: "Explore the network",
  },
  network: {
    eyebrow: "The network",
    title: "A small structure, kept deliberately shallow.",
    body: [
      "A Growth Partner builds and leads the network in a territory. Growth Leaders develop and support groups of Ambassadors. Ambassadors work directly with the communities they already belong to.",
      "A Growth Partner may also work directly with Ambassadors. There is no requirement to build a layer that the territory does not need.",
    ],
    listTitle: "Territory is not exclusivity",
    list: [
      { title: "{{Territory}} — General" },
      { title: "{{Territory}} — Academies" },
      { title: "{{Territory}} — Education" },
      { title: "{{Territory}} — Creators" },
    ],
    highlight: "People aren't owned by the network.",
    closing: [
      "More than one Growth Partner may operate in the same territory, working on different segments or in different ways.",
      "Recruiting someone does not automatically create permanent management ownership of them. Relationships are recorded and preserved, but people are not property.",
    ],
    cta: "Meet the people who can become Ambassadors",
  },
  "types-and-grades": {
    eyebrow: "Types and grades",
    title: "Different people contribute in different ways.",
    body: [
      "Every Ambassador holds one primary type, and may hold further secondary types and specialisations as their contribution broadens.",
    ],
    listTitle: "How your contribution develops",
    closing: [
      "Type is how you contribute. Grade is how your contribution develops. Network role is the responsibility you hold. They are independent of each other.",
      "Progression considers growth, customer quality, consistency, trust and contribution — not volume alone.",
    ],
    cta: "See how the economics work",
  },
  economics: {
    eyebrow: "The economics",
    title: "Create genuine customer value. Share in the value created.",
    body: [
      "Direct commission is a share of the subscription revenue from customers you bring to Zhatura, over the first three years of that subscription.",
    ],
    highlight: "No paying customer means no recurring network commission.",
    closing: [
      "Recruiting an Ambassador alone does not create recurring earnings.",
      "Exact contractual terms are presented separately as part of an individual offer and agreement. Nothing in this journey is an offer, and every figure here is an illustration.",
    ],
    cta: "Explore your potential role",
  },
  "your-potential-role": {
    eyebrow: "Your potential role",
    title: "Help build Zhatura's first growth ecosystem in {{Territory}}.",
    body: [
      "This is not intended to be a traditional sales role. It is closer to founding a local organisation: choosing who to work with, deciding where to start, and building something that keeps working after you have built it.",
    ],
    listTitle: "Six pillars",
    list: [
      {
        title: "Build the Ambassador network",
        detail: "Find, recommend and develop the right people.",
      },
      {
        title: "Develop the market",
        detail: "Understand where chess actually happens in {{Territory}}.",
      },
      {
        title: "Build partnerships",
        detail: "Academies, schools, clubs, tournaments and communities.",
      },
      {
        title: "Execute growth campaigns",
        detail: "Run them locally, with Zhatura's support behind you.",
      },
      {
        title: "Bring market intelligence",
        detail:
          "Tell us what is true on the ground, including what isn't working.",
      },
      {
        title: "Represent Zhatura",
        detail: "Carefully, and in a way the community respects.",
      },
    ],
    secondaryTitle: "What we don't want you spending your time on",
    secondaryList: [
      { title: "Spreadsheets" },
      { title: "Calculating commissions" },
      { title: "Manually generating links" },
      { title: "Routine reports" },
      { title: "Manually tracking referrals" },
      { title: "Repeatedly explaining onboarding" },
    ],
    highlight:
      "You focus on people and growth. Zhatura handles the infrastructure around you.",
    cta: "See the workspace designed to support you",
  },
  "growth-workspace": {
    eyebrow: "Growth workspace",
    title: "The work should already be organised when you sit down.",
    body: [
      "Your workspace shows what is happening in your network and what deserves your attention today. The numbers below are a demonstration, not real data.",
    ],
    highlight:
      "Network visibility does not mean access to private player information.",
    cta: "Meet the intelligence behind the workspace",
  },
  "ai-and-automation": {
    eyebrow: "AI and automation",
    title: "Imagine having a growth analyst beside you every day.",
    body: [
      "Zhatura Growth AI reads what is happening across your network and tells you where your attention is worth most. You stay in charge of every decision.",
    ],
    listTitle: "What it does",
    list: [
      {
        title: "Candidate intelligence",
        detail: "Prepare properly before you meet someone.",
      },
      {
        title: "Network health",
        detail: "Notice where support is needed early.",
      },
      {
        title: "Campaign recommendations",
        detail: "Where a campaign would actually land.",
      },
      {
        title: "Content assistance",
        detail: "Drafts you edit, never messages sent for you.",
      },
      {
        title: "Market intelligence",
        detail: "Patterns across territories and segments.",
      },
      {
        title: "Reporting",
        detail: "Reports written without you writing them.",
      },
      {
        title: "Next-best actions",
        detail: "A short, honest list for the week.",
      },
      {
        title: "Ambassador support",
        detail: "Answers for your network, at any hour.",
      },
    ],
    highlight: "AI assists. People decide.",
    closing: [
      "Final application decisions, sensitive compliance decisions, contracts and important exceptions remain human-controlled.",
      "Our goal isn't to automate relationships. It's to automate the administration around relationships.",
    ],
    cta: "See how success will be measured",
  },
  success: {
    eyebrow: "Success",
    title: "A large network isn't necessarily a successful network.",
    body: [
      "We would rather have a smaller network of people doing real work than a large one that looks impressive and produces nothing.",
    ],
    listTitle: "What we measure",
    list: [
      { title: "Subscription growth", detail: "Real customers, paying." },
      { title: "Active Ambassadors", detail: "Not registered — active." },
      { title: "Ambassador productivity", detail: "Contribution per person." },
      { title: "Customer quality", detail: "Customers who stay." },
      { title: "Partnerships", detail: "Academies, schools and communities." },
      {
        title: "Geographic coverage",
        detail: "Where {{Territory}} is and isn't served.",
      },
      {
        title: "Campaign execution",
        detail: "What was run, and what it produced.",
      },
      {
        title: "Market intelligence",
        detail: "What we learned that we could not have guessed.",
      },
      { title: "Trust and compliance", detail: "How the network behaves." },
    ],
    closing: [
      "The same measurements are visible to you and to Zhatura. There is no hidden scorecard.",
      "Founding recognition reflects the people who helped us build the beginning. It remains part of that history even as responsibilities, territories and commercial arrangements evolve over time.",
    ],
    cta: "Prepare for our conversation",
  },
  "your-perspective": {
    eyebrow: "Your perspective",
    title: "Now we'd like to hear from you.",
    body: [
      "Answer whichever of these you want to. Short notes are completely fine, and anything you'd rather talk through you can mark for the conversation instead. Your answers save as you type.",
    ],
    cta: "I'm ready for the discussion",
  },
};

export const ui = {
  progress: "Step {{current}} of {{total}}",
  invitationMeta: [
    "About 15 minutes",
    "Private introduction",
    "No commitment required",
  ],
  back: "Back",
  saving: "Saving…",
  saved: "Saved",
  saveFailed: "Not saved yet — we'll try again",
  saveAnswer: "Save answer",
  discussInstead: "Discuss during interview",
  discussSelected: "Marked for the conversation",
  answerPlaceholder: "Your thoughts…",
  askZhatura: "Ask Zhatura",
  askZhaturaTitle: "Ask Zhatura",
  askZhaturaIntro:
    "Ask anything about the role, the network, the economics or this journey.",
  askPlaceholder: "What would you like to know?",
  askSubmit: "Ask",
  askThinking: "Thinking…",
  askClose: "Close",
  askUnavailable:
    "I don't have an approved answer to that yet. That's a good question to discuss during your meeting with Zhatura.",
  demonstrationData: "Demonstration data",
  completionTitle: "Thank you, {{FirstName}}.",
  completionLede: "You're ready for our conversation.",
  meetingTitle: "Your conversation",
  addToCalendar: "Add to calendar",
  meetingPending:
    "We'll be in touch shortly to arrange a time that works for you.",
  reviewJourney: "Read the journey again",
  illustrationNote:
    "Illustrative only. Not an offer and not a contractual term.",
  yearLabel: "Year {{year}}",
  yearOnwards: "Year {{year}}+",
  exampleTitle: "An example",
  exampleLede:
    "On eligible subscription revenue of {{revenue}} per year, direct commission would be:",
  exampleTotal: "Total across the example",
  flowTitle: "Where the money comes from",
  overridesTitle: "Network override",
  componentsTitle: "For a Founding Growth Partner",
  loading: "Opening your invitation…",
  errorTitle: "This invitation can't be opened",
  errorGeneric:
    "Something went wrong opening this invitation. Please try the link again, or contact the person who sent it to you.",
  errorMissing:
    "This link is missing its private invitation code. Please open the original link exactly as it was sent to you.",
  errorNotFound:
    "We couldn't find this invitation. Please check you opened the original link exactly as it was sent to you.",
  errorExpired:
    "This invitation has expired. Please contact the person at Zhatura who invited you and they can send a new one.",
  errorRevoked:
    "This invitation is no longer active. Please contact the person at Zhatura who invited you.",
  errorRateLimited:
    "Too many attempts just now. Please wait a moment and try again.",
  errorOffline:
    "We couldn't reach Zhatura. Please check your connection and try again.",
  retry: "Try again",
};

export const workspaceDemo = {
  metrics: [
    { title: "Active Ambassadors", detail: "37" },
    { title: "Growth Leaders", detail: "4" },
    { title: "New subscribers", detail: "143" },
    { title: "Network revenue", detail: "INR 428K" },
  ],
  prioritiesTitle: "Today's priorities",
  priorities: [
    { title: "4 Ambassador candidates need recommendation" },
    { title: "3 academy opportunities need follow-up" },
    { title: "Improve coverage in Madurai" },
    { title: "{{Territory}} tournament opportunity" },
  ],
  networkTitle: "Your network",
  network: [
    { title: "{{Territory}}", detail: "Whole territory" },
    { title: "Chennai", detail: "14 Ambassadors" },
    { title: "Coimbatore", detail: "9 Ambassadors" },
    { title: "Madurai", detail: "5 Ambassadors" },
    { title: "Other", detail: "9 Ambassadors" },
  ],
  earningsTitle: "Earnings",
};

export const growthAiDemo = {
  question: "What should I focus on this week?",
  answers: [
    {
      title: "Academy development in Coimbatore",
      detail: "Three academies have active enquiries and no follow-up yet.",
    },
    {
      title: "Ambassador candidate review",
      detail: "Four candidates have completed their journey and are waiting.",
    },
    {
      title: "Madurai coverage",
      detail:
        "Growth is steady but coverage is thin relative to the population.",
    },
  ],
};

export function format(
  template: string,
  values: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key)
      ? (values[key] ?? match)
      : match,
  );
}
